import nextUid from "__common/calculations/nextUid";
import { createArrayGrid } from "./dividePanelsintoSubArrays";

export interface PanelBayMappingResult {
  invalidatedBayIds: number[];
  leftEdgePanels: number[];
  mappingChanged: boolean;
  northEdgePanels: number[];
  northMostRow: number[];
  northRightEdgePanels: number[];
  rightEdgePanels: number[];
}

export const createPanelBayMapping = (
  array,
  panelsDict: { [key: number]: panelInState },
  panelBayMapping: panelBayMapping = new Map(),
): PanelBayMappingResult => {
  const result: PanelBayMappingResult = {
    invalidatedBayIds: [],
    leftEdgePanels: [],
    mappingChanged: false,
    northEdgePanels: [],
    northMostRow: [],
    northRightEdgePanels: [],
    rightEdgePanels: [],
  };

  if (!Array.isArray(array) || array.length < 1) {
    return result;
  }

  const grid = createArrayGrid(array);
  const arr = grid?.map(subarray => subarray.map(({ id }) => id));

  if (!arr?.length) {
    return result;
  }

  const arrayMapping: panelBayMapping = new Map();

  arr.forEach((row, rowIndex, arr) => {
    row.forEach((panel, eleIndex) => {
      if (panel && panelBayMapping.has(panel)) {
        arrayMapping.set(panel, panelBayMapping.get(panel));
      }

      if (eleIndex !== arr[0].length - 1 && rowIndex !== arr.length - 1 && arr[rowIndex][eleIndex + 1] === undefined && !arr[rowIndex + 1][eleIndex + 1]) {
        panel && result.rightEdgePanels.push(row[eleIndex]);
      } else if (panel && eleIndex !== arr[0].length - 1 && rowIndex === arr.length - 1 && arr[rowIndex][eleIndex + 1] === undefined) {
        result.rightEdgePanels.push(panel);
      } else if (eleIndex === arr[0].length - 1 && panel) {
        result.rightEdgePanels.push(panel);
      }

      if (eleIndex !== 0 && rowIndex !== arr.length - 1 && arr[rowIndex][eleIndex - 1] === undefined && !arr[rowIndex + 1][eleIndex - 1]) {
        panel && result.leftEdgePanels.push(panel);
      } else if (eleIndex !== 0 && rowIndex === arr.length - 1 && arr[rowIndex][eleIndex - 1] === undefined) {
        panel && result.leftEdgePanels.push(panel);
      } else if (eleIndex === 0 && panel) {
        result.leftEdgePanels.push(panel);
      }

      if (rowIndex !== 0 && arr[rowIndex - 1][eleIndex] === undefined && (!arr[rowIndex - 1][eleIndex + 1] || !arr[rowIndex - 1][eleIndex - 1])) {
        panel && result.northEdgePanels.push(panel);
      }

      if (
        panel &&
        rowIndex !== 0 &&
        arr[rowIndex][eleIndex + 1] === undefined &&
        arr[rowIndex - 1][eleIndex + 1] === undefined
      ) {
        result.northRightEdgePanels.push(panel);
      }

      if (
        panel &&
        eleIndex === row.length - 1 &&
        rowIndex !== arr.length - 1 &&
        arr[rowIndex + 1] &&
        eleIndex + 1 <= arr[rowIndex + 1].length - 1
      ) {
        result.rightEdgePanels.push(panel);
      }
    });
  });

  const leftMostPanel = arr[arr.length - 1].find(panel => panel !== undefined);
  const rightMostPanel = arr[arr.length - 1].slice().reverse().find(panel => panel !== undefined);

  if (leftMostPanel !== undefined) {
    result.leftEdgePanels.push(leftMostPanel);
  }

  if (rightMostPanel !== undefined) {
    result.rightEdgePanels.push(rightMostPanel);
  }

  const reversedArr = arr[0].slice().reverse();
  arr[0].forEach((panel, i) => {
    if (panel !== undefined) {
      result.northEdgePanels.push(panel);
    }

    if (panel === undefined && i !== 0 && arr[0][i - 1]) {
      result.northRightEdgePanels.push(arr[0][i - 1]);
      if (arr[1] && arr[1][i] === undefined) {
        result.northMostRow.push(arr[i - 1]);
      }
    }
  });

  const index = reversedArr.findIndex(panel => panel !== undefined);
  if (index !== -1 && arr[1]?.[arr[0].length - 1 - index + 1] === undefined) {
    reversedArr.splice(index, 1);
  }

  if (array.length === arrayMapping.size) {
    return result;
  }

  result.mappingChanged = true;
  arrayMapping.forEach((baysAssociated) => {
    baysAssociated?.forEach((bay) => result.invalidatedBayIds.push(bay.id));
  });

  const bayId = nextUid;

  for (let i = 0; i < arr.length; i++) {
    const row = arr[i];
    for (let j = 0; j < row.length; j++) {
      if (arr[i][j] && !panelBayMapping.has(arr[i][j])) {
        const temp = [];
        if (arr[i][j - 1]) {
          temp.push({ ...panelBayMapping.get(arr[i][j - 1])[1], p: 1 });
          temp.push({ id: bayId(), p: 2 });
          temp.push({ id: bayId(), p: 3 });
          temp.push({ ...panelBayMapping.get(arr[i][j - 1])[2], p: 4 });
          if (arr[i][j + 1] && panelBayMapping.has(arr[i][j + 1])) {
            temp[1] = { ...panelBayMapping.get(arr[i][j + 1])[0], p: 2 };
            temp[2] = { ...panelBayMapping.get(arr[i][j + 1])[3], p: 3 };
          } else if (i !== 0 && arr[i - 1][j] && panelBayMapping.has(arr[i - 1][j]) && arr[i + 1]?.[j] && panelBayMapping.has(arr[i + 1][j])) {
            temp[0] = { ...panelBayMapping.get(arr[i][j - 1])[1], p: 1 };
            temp[1] = { ...panelBayMapping.get(arr[i - 1][j])[2], p: 2 };
            temp[2] = { ...panelBayMapping.get(arr[i + 1][j])[1], p: 3 };
            temp[3] = { ...panelBayMapping.get(arr[i][j - 1])[2], p: 4 };
          } else if (i !== 0 && arr[i - 1][j] && panelBayMapping.has(arr[i - 1][j])) {
            temp[0] = { ...panelBayMapping.get(arr[i][j - 1])[1], p: 1 };
            temp[1] = { ...panelBayMapping.get(arr[i - 1][j])[2], p: 2 };
            temp[3] = { ...panelBayMapping.get(arr[i][j - 1])[2], p: 4 };
          } else if (i !== 0 && arr[i - 1][j + 1] && panelBayMapping.has(arr[i - 1][j + 1])) {
            temp[1] = { ...panelBayMapping.get(arr[i - 1][j + 1])[3], p: 2 };
          } else if (arr[i + 1]?.[j] && panelBayMapping.has(arr[i + 1][j])) {
            temp[2] = { ...panelBayMapping.get(arr[i + 1][j])[1], p: 3 };
          }
        } else if (arr[i][j - 1] && !arr[i - 1][j]) {
          if (arr[i - 1][j + 1]) {
            temp.push({ ...panelBayMapping.get(arr[i][j - 1])[4], p: 4 });
          } else {
            temp.push({ ...panelBayMapping.get(arr[i][j - 1])[1], p: 1 });
            temp.push({ id: bayId(), p: 2 });
            temp.push({ id: bayId(), p: 3 });
          }
        } else if (i !== 0 && arr[i - 1][j]) {
          temp.push({ ...panelBayMapping.get(arr[i - 1][j])[3], p: 1 });
          temp.push({ ...panelBayMapping.get(arr[i - 1][j])[2], p: 2 });
          temp.push({ id: bayId(), p: 3 });
          temp.push({ id: bayId(), p: 4 });
          if (i !== arr.length - 1 && arr[i + 1][j] && panelBayMapping.has(arr[i + 1][j])) {
            temp[2] = { ...panelBayMapping.get(arr[i + 1]?.[j])[1], p: 3 };
            temp[3] = { ...panelBayMapping.get(arr[i + 1]?.[j])[0], p: 4 };
          } else if (arr[i][j + 1] && panelBayMapping.has(arr[i]?.[j + 1])) {
            temp[2] = { ...panelBayMapping.get(arr[i]?.[j + 1])[3], p: 3 };
          }
        } else if (!arr[i][j - 1] && i !== 0 && arr[i - 1][j - 1]) {
          temp.push({ ...panelBayMapping.get(arr[i - 1][j - 1])[2], p: 1 });
          temp.push({ id: bayId(), p: 2 });
          temp.push({ id: bayId(), p: 3 });
          temp.push({ id: bayId(), p: 4 });
        } else if (arr[i][j + 1] && panelBayMapping.has(arr[i][j + 1])) {
          temp.push({ id: bayId(), p: 1 });
          temp.push({ ...panelBayMapping.get(arr[i][j + 1])[0], p: 2 });
          temp.push({ ...panelBayMapping.get(arr[i][j + 1])[3], p: 3 });
          temp.push({ id: bayId(), p: 4 });
          if (arr[i + 1][j] && panelBayMapping.has(arr[i + 1][j])) {
            temp[3] = { ...panelBayMapping.get(arr[i + 1][j])[0], p: 4 };
          }
        } else if (i !== 0 && arr[i - 1][j + 1]) {
          temp.push({ id: bayId(), p: 1 });
          temp.push({ ...panelBayMapping.get(arr[i - 1][j + 1])[3], p: 2 });
          temp.push({ id: bayId(), p: 3 });
          temp.push({ id: bayId(), p: 4 });
        } else if (i === 0 && i !== arr.length - 1 && arr[i + 1][j] && panelBayMapping.has(arr[i + 1][j])) {
          temp.push({ id: bayId(), p: 1 });
          temp.push({ id: bayId(), p: 2 });
          temp.push({ ...panelBayMapping.get(arr[i + 1][j])[1], p: 3 });
          temp.push({ ...panelBayMapping.get(arr[i + 1][j])[0], p: 4 });
        } else {
          temp.push({ id: bayId(), p: 1 });
          temp.push({ id: bayId(), p: 2 });
          temp.push({ id: bayId(), p: 3 });
          temp.push({ id: bayId(), p: 4 });
        }

        panelBayMapping.set(arr[i][j], temp);
        const selection = panelsDict[arr[i][j]];
        selection?.manualAttachments?.forEach((value, index) => {
          temp[index].attached = value;
        });
      }
    }
  }

  return result;
};
